import { useRef } from 'react';
import { DateTime, Num, T, Var } from 'gt-react';
import { UploadSimpleIcon } from '@phosphor-icons/react';
import type { AgreementExtractSummary } from '@erp/rpc';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

export function AgreementExtractPanel({
  summary,
  isLoading,
  isError,
  canUpload,
  isUploading,
  progress,
  onPick,
}: {
  summary: AgreementExtractSummary | null;
  isLoading: boolean;
  isError: boolean;
  canUpload: boolean;
  isUploading: boolean;
  progress: number | null;
  onPick: (file: File) => void;
}) {
  return (
    <div className="space-y-3">
      <div>
        <h2 className="font-medium">
          <T context="Pricing reference data page, agreement extract heading">
            AS400 Agreement Extract
          </T>
        </h2>
        <p className="text-muted-foreground text-sm">
          <T context="Pricing reference data page, agreement extract description">
            Candidate agreement numbers from AGREE_DLR, joined by vendor, price
            class, and overlapping date window.
          </T>
        </p>
      </div>

      <AgreementExtractStatus
        summary={summary}
        isLoading={isLoading}
        isError={isError}
      />

      {canUpload ? (
        <AgreementExtractUpload
          hasExtract={summary !== null}
          isUploading={isUploading}
          progress={progress}
          onPick={onPick}
        />
      ) : null}
    </div>
  );
}

function AgreementExtractStatus({
  summary,
  isLoading,
  isError,
}: {
  summary: AgreementExtractSummary | null;
  isLoading: boolean;
  isError: boolean;
}) {
  if (isLoading) {
    return (
      <p className="text-muted-foreground text-sm">
        <T context="Pricing agreement extract loading state">Loading…</T>
      </p>
    );
  }
  if (isError) {
    return (
      <p className="text-destructive text-sm">
        <T context="Pricing agreement extract error state">
          Could not read the agreement extract currently in force.
        </T>
      </p>
    );
  }
  if (!summary) {
    return (
      <p className="text-muted-foreground text-sm">
        <T context="Pricing agreement extract empty state">
          No agreement extract has been checked yet. SPA records will say so
          until an all-company, all-division AGREE_DLR CSV is loaded.
        </T>
      </p>
    );
  }

  return (
    <div className="space-y-1 text-sm">
      <p className="font-medium">
        <Var>{summary.fileName}</Var>
      </p>
      <p className="text-muted-foreground">
        <T context="Pricing agreement extract loaded row count">
          <Num>{summary.agreementRowCount}</Num> price-class rows from{' '}
          <Num>{summary.sourceRowCount}</Num> source rows, covering{' '}
          <Num>{summary.vendorPrefixes.length}</Num> vendor prefixes.
        </T>
      </p>
      <p className="text-muted-foreground">
        {summary.uploadedBy ? (
          <T context="Pricing agreement extract upload time and uploader">
            Uploaded{' '}
            <DateTime options={{ dateStyle: 'medium', timeStyle: 'short' }}>
              {summary.uploadedAt}
            </DateTime>{' '}
            by <Var>{summary.uploadedBy}</Var>
          </T>
        ) : (
          <T context="Pricing agreement extract upload time">
            Uploaded{' '}
            <DateTime options={{ dateStyle: 'medium', timeStyle: 'short' }}>
              {summary.uploadedAt}
            </DateTime>
          </T>
        )}
      </p>
    </div>
  );
}

function AgreementExtractUpload({
  hasExtract,
  isUploading,
  progress,
  onPick,
}: {
  hasExtract: boolean;
  isUploading: boolean;
  progress: number | null;
  onPick: (file: File) => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) onPick(file);
  }

  return (
    <div className="space-y-3">
      <input
        ref={fileInput}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={handleChange}
      />
      <Button onClick={() => fileInput.current?.click()} disabled={isUploading}>
        <UploadSimpleIcon className="mr-2 size-4" />
        {hasExtract ? (
          <T context="Pricing agreement extract upload button">
            Replace Agreement Extract
          </T>
        ) : (
          <T context="Pricing agreement extract upload button">
            Upload Agreement Extract
          </T>
        )}
      </Button>
      {progress !== null ? <Progress value={progress} /> : null}
      {isUploading && progress === null ? (
        <p className="text-muted-foreground text-sm">
          <T context="Pricing agreement extract processing state">
            Reading the CSV…
          </T>
        </p>
      ) : null}
    </div>
  );
}
